import { createSlice } from '@reduxjs/toolkit'
import { API } from 'src/utils/interceptor'
import Sentry from 'src/utils/sentry'
import dayjs from 'dayjs'
import moment from 'moment'
import _ from 'underscore'
import { getVenueOrderUnitPrice } from 'src/utils/priceFunctions'
import { isOrderableBasketLine } from 'src/utils/basketLines'

const initialState = {
  orders: [],
  basket: [],
  statementOrders: [],
  filteredStatementOrders: [],
  filtered: [],
  orderedProduct: [],
  creditNotes: [],
  creditNotesTotal: 0,
  creditNotesPagination: { page: 1, limit: 50 },
  isLoading: false,
  isUpdateLoading: false,
  isCreditHistoryLoading: false,
  venueOrders: [],
  error: '',
  dateGrouped: [],
  dateOrdersLoading: false,
  spendOverTimeTotals: {},
  simpliifiedOrders: [],
  checkoutSummary: null,
  checkoutSummaryLoading: false,
  checkoutSummaryError: null,
}

const filterOrders = orders => {
  const filterredOrders = orders?.map(order => {
    // CSV reflects originally ordered quantity (short deliveries included);
    // received quantities live in Goods Receipting, not the Orders export.
    const quantity = order?.receipting?.orderedQuantity || order?.quantity || 1
    const price = getVenueOrderUnitPrice(order)
    return {
      PO: order?.purchaseOrderNumber,
      Title: order?.title || order?.product?.title,
      sku: order?.product?.sku || '',
      Quantity: quantity,
      OrderDate: dayjs(order?.orderedDate).format('DD/MM/YYYY'),
      ItemPrice: `£${Number(price).toFixed(2)}`,
      TotalPrice: `£${Number(price * quantity).toFixed(2)}`,
      Venue: order?.venue?.title,
      Buyer: order?.placedVia ?? 'Placed using a venue code',
      Supplier: order?.product?.supplier?.title,
      Status:
        order?.supplierConfirmed === true
          ? 'Confirmed'
          : order?.supplierConfirmed === undefined
          ? 'Order Received'
          : order?.supplierConfirmed === false
          ? 'Rejected'
          : '',
    }
  })
  return filterredOrders
}

/**
 * Helper function to deduplicate orders by _id using a Map
 * This ensures idempotent basket operations and prevents duplicate entries
 */
const deduplicateById = orders => {
  if (!Array.isArray(orders)) return []
  const orderMap = new Map()
  orders.forEach(order => {
    if (order && order._id) {
      orderMap.set(order._id, order)
    }
  })
  return Array.from(orderMap.values())
}

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setStatementOrders: (state, action) => {
      state.statementOrders = action.payload
      state.filteredStatementOrders = state.statementOrders
    },
    setVenueOrders: (state, action) => {
      state.venueOrders = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
    },
    set: (state, action) => {
      // Use Map-based deduplication to prevent duplicate orders
      const ordersMap = new Map()
      const basketMap = new Map()

      if (action.payload && action.payload.length > 0) {
        action.payload
          ?.filter(order => order?.nonOMSSupplier !== true && order?._id)
          .reverse()
          .forEach(order => {
            if (order.isOrdered) {
              ordersMap.set(order._id, order)
            } else if (isOrderableBasketLine(order)) {
              basketMap.set(order._id, order)
            }
          })
      }

      state.orders = Array.from(ordersMap.values())
      state.basket = Array.from(basketMap.values())
      state.filtered = state.orders
    },
    edit: (state, action) => {
      const index = state.orders.findIndex(
        order => order._id === action.payload._id,
      )
      if (index >= 0) {
        state.orders[index] = action.payload
      }
    },
    setBasket: (state, action) => {
      // Deduplicate basket items by _id when setting
      state.basket = deduplicateById(action.payload).filter(
        isOrderableBasketLine,
      )
    },
    updateOrder: (state, action) => {
      if (!action.payload?._id) return

      const index = state.basket.findIndex(
        order => order._id === action.payload._id,
      )
      if (index >= 0) {
        if (action.payload.quantity === 0) {
          state.basket.splice(index, 1)
        } else {
          // Update existing order in place
          state.basket[index] = action.payload
        }
      } else if (
        action.payload.quantity !== 0 &&
        isOrderableBasketLine(action.payload)
      ) {
        // Only add if not already present and quantity > 0
        state.basket.push(action.payload)
      }
    },
    updateFilteredStatement: (state, action) => {
      state.filteredStatementOrders = action.payload
    },
    updateFilteredOrders: (state, action) => {
      state.filtered = [...action.payload]
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload
    },
    setUpdateLoading: (state, action) => {
      state.isUpdateLoading = action.payload
    },
    setCreditHistoryLoading: (state, action) => {
      state.isCreditHistoryLoading = action.payload
    },
    setOrderedProduct: (state, action) => {
      state.orderedProduct = [...action.payload]
    },
    setCreditNotes: (state, action) => {
      state.creditNotes = [...action.payload]
    },
    setCreditNotesPagination: (state, action) => {
      state.creditNotesPagination = action.payload
    },
    setCreditNotesTotal: (state, action) => {
      state.creditNotesTotal = action.payload
    },
    resetOrders: state => {
      state = initialState
      return state
    },
    setLoadingElementId: (state, action) => {
      state.loadingElementId = action.payload
    },
    updateCreditsNotes: (state, action) => {
      const { receipting } = action.payload

      state.creditNotes = state.creditNotes.map(orderNote => {
        if (receipting._id === orderNote._id.receipting[0]._id) {
          return {
            _id: {
              ...orderNote._id,
              receipting: [receipting],
            },
          }
        }
        return orderNote
      })
    },
    findOrdersAndUpdate: (state, action) => {
      state.orders = state.filtered.map(order => {
        const foundedOrder = action.payload.find(
          receipting => order._id === receipting.order,
        )
        const res =
          foundedOrder !== undefined
            ? { ...order, receipting: foundedOrder._id }
            : order

        return res
        // return order;
      })
    },
    setDateGrouped: (state, action) => {
      state.dateGrouped = action.payload
    },
    setDateOrdersLoading: (state, action) => {
      state.dateOrdersLoading = action.payload
    },
    setSpendOverTimeTotals: (state, action) => {
      state.spendOverTimeTotals = action.payload
    },
    setSimplifiedOrders: (state, action) => {
      state.simpliifiedOrders = action.payload
    },
    setCheckoutSummary: (state, action) => {
      state.checkoutSummary = action.payload
      state.checkoutSummaryLoading = false
      state.checkoutSummaryError = null
    },
    setCheckoutSummaryLoading: (state, action) => {
      state.checkoutSummaryLoading = action.payload
    },
    setCheckoutSummaryError: (state, action) => {
      state.checkoutSummaryError = action.payload
      state.checkoutSummaryLoading = false
    },
  },
})

export const {
  set,
  setVenueOrders,
  edit,
  setBasket,
  updateFilteredOrders,
  updateFilteredStatement,
  updateOrder,
  setLoading,
  setUpdateLoading,
  resetOrders,
  setOrderedProduct,
  setStatementOrders,
  findOrdersAndUpdate,
  setCreditNotesPagination,
  setCreditNotes,
  setCreditHistoryLoading,
  setCreditNotesTotal,
  updateCreditsNotes,
  setError,
  setDateGrouped,
  setDateOrdersLoading,
  setSpendOverTimeTotals,
  setSimplifiedOrders,
  setCheckoutSummary,
  setCheckoutSummaryLoading,
  setCheckoutSummaryError,
} = ordersSlice.actions

export const createOrder = body => async dispatch => {
  dispatch(setUpdateLoading(true))
  try {
    const order = await API.post('api2', `order`, { body })
    const removed =
      Number(body?.quantity) === 0
        ? { _id: body?._id || order?._id, quantity: 0 }
        : null
    const next = removed || order
    if (next?._id) dispatch(updateOrder(next))
    return order
  } catch (error) {
    if (Number(body?.quantity) === 0 && body?._id) {
      dispatch(updateOrder({ _id: body._id, quantity: 0 }))
    }
    console.error(error)
    Sentry.captureException(error)
    return false
  } finally {
    dispatch(setUpdateLoading(false))
  }
}

export const findOrderAndUpdateState = receiptingArr => async dispatch => {
  dispatch(findOrdersAndUpdate(receiptingArr))
}

export const fetchStatementOrders =
  (venueId = '', start = '', end = '', suppliers = [], types = []) =>
  async dispatch => {
    // Cleared date pickers send null; default params only cover undefined.
    const normalizedStart = (start || '').split('/').reverse().join('-')
    const normalizedEnd = (end || '').split('/').reverse().join('-')

    console.log(
      'venueId',
      venueId,
      'start',
      normalizedStart,
      'end',
      normalizedEnd,
    )
    await API.post(
      'api',
      `venue/${venueId}/statements?start=${normalizedStart}&end=${normalizedEnd}`,
      {
        body: { suppliers, types },
      },
    ).then(data => {
      dispatch(setStatementOrders(data))
      return data
    })
  }

export const getCreditHistory = (venueId, pagination) => async dispatch => {
  try {
    dispatch(setCreditHistoryLoading(true))
    const result = await API.get(
      'api',
      `receipting/${venueId}/credits-history?page=${pagination.page}&limit=${pagination.limit}`,
    )

    dispatch(setCreditNotes(result.ordersArr))
    dispatch(setCreditNotesTotal(result.ordersTotalCount))
    dispatch(setCreditNotesPagination(pagination))
    dispatch(setCreditHistoryLoading(false))
  } catch (e) {
    console.log(e)
    Sentry.captureException(e)
    dispatch(setCreditHistoryLoading(false))

    return false
  }
}
export const clearCreditNotesHistory = () => async dispatch => {
  dispatch(setCreditNotes([]))
}
/**
 * @deprecated Legacy order sending function - DO NOT USE
 * This function uses the legacy orders-router endpoint which bypasses
 * the new checkout guardrails (basket versioning, idempotency, etc.)
 *
 * Use confirmOrder() instead for all order confirmations.
 * This function is kept only for backwards compatibility and will be removed.
 */
export const sendOrder = (venueId, body) => async dispatch => {
  dispatch(setLoading(true))

  try {
    await API.post('api', `venue/${venueId}/orders-router`, {
      body,
    })
    dispatch(setBasket([]))
    dispatch(fetchOrders(venueId))
    dispatch(setLoading(false))
    return { success: true }
  } catch (error) {
    dispatch(setLoading(false))
    console.error(error)
    throw error
  }
}

export const fetchOrders =
  (venueId = '', start = '', end = '', suppliers = [], types = []) =>
  async dispatch => {
    try {
      dispatch(setLoading(true))
      if (venueId && start && end) {
        const normalizedStart = start.split('/').reverse().join('-')
        const normalizedEnd = end.split('/').reverse().join('-')
        const url = `orders/${venueId}?start=${normalizedStart}&end=${normalizedEnd}`
        const data = await API.post('api2', url, { body: { suppliers, types } })
        const dateGrouped = _(data?.simplifiedOrders).groupBy(
          o =>
            o?.orderedDate?.split('T')[0] ||
            moment
              .utc(o.orderedDate)
              .local()
              .startOf('day')
              .format('YYYY-MM-DD'),
        )
        dispatch(setSimplifiedOrders(data?.simplifiedOrders))
        dispatch(setDateGrouped(dateGrouped))
        dispatch(set(data?.latestOrders))
      } else if (venueId) {
        const url = `venue/${venueId}/orders`
        const data = await API.get('api', url)
        const filteredOrders = data.filter(
          order => order && order?.nonOMSSupplier !== true,
        )
        dispatch(set(filteredOrders))
      }
    } catch (e) {
      console.log('-------error-----', e.message)

      dispatch(setError(e.message))
      console.error(e)
      Sentry.captureException(e)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  }

export const fetchOrdersByIds = (orderIds, venueId) => async dispatch => {
  dispatch(setDateOrdersLoading(true))
  try {
    const url = `orders/${venueId}/getOrdersByIds`
    const data = await API.post('api2', url, { body: { orderIds } })
    dispatch(set(data?.orders))
    dispatch(setDateOrdersLoading(false))
  } catch (e) {
    console.log('-------error-----', e.message)
    dispatch(setError(e.message))
    console.error(e)
    dispatch(setDateOrdersLoading(false))
    Sentry.captureException(e)
    return false
  }
}

export const fetchSpendOverTimeTotals =
  (venueId = '', start = '', end = '', suppliers = [], types = []) =>
  async dispatch => {
    try {
      const normalizedStart = start.split('/').reverse().join('-')
      const normalizedEnd = end.split('/').reverse().join('-')
      const data = await API.post(
        'api2',
        `orders/${venueId}/spend-over-time?start=${normalizedStart}&end=${normalizedEnd}`,
        {
          body: { suppliers, types },
        },
      )
      const spendOverTimeTotals = {
        bySuppliers: data?.bySuppliers || [],
        byMonths: data?.byMonths || [],
        grandTotal: data?.grandTotal || 0,
        // Inc-VAT totals for the Purchasing VAT toggle; undefined until the
        // express_app response ships them, and pickVatTotal falls back to ex.
        grandTotalIncVat: data?.grandTotalIncVat,
      }
      dispatch(setSpendOverTimeTotals(spendOverTimeTotals))
    } catch (e) {
      console.error(e)
      Sentry.captureException(e)
    }
  }

export const fetchVenueOrders = async (venueIds = [], start = '', end = '') => {
  try {
    if (venueIds.length && start && end) {
      const normalizedStart = start.split('/').reverse().join('-')
      const normalizedEnd = end.split('/').reverse().join('-')
      const url = `venues/orders?start=${normalizedStart}&end=${normalizedEnd}`
      const { data } = await API.post('api2', url, {
        body: { venueIds },
      })
      const newOrders = filterOrders(data)
      return newOrders
    }
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}

export const editOrder = data => async dispatch => {
  try {
    const _order = await API.post('api', `order`, {
      body: data,
    })

    dispatch(edit(_order))
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
    return false
  }
}
export const updateFiltered = array => dispatch =>
  dispatch(updateFilteredOrders(array))

export const updateStatementFiltered = array => dispatch => {
  dispatch(updateFilteredStatement(array))
}

// Fetch pending order summary for checkout
export const fetchPendingOrderSummary = venueId => async dispatch => {
  try {
    dispatch(setCheckoutSummaryLoading(true))
    dispatch(setCheckoutSummaryError(null))

    const url = `venue/${venueId}/pending-order-summary`
    const data = await API.get('api', url)

    dispatch(setCheckoutSummary(data))
    return data
  } catch (e) {
    const errorMessage = e.message || 'Failed to fetch checkout summary'
    console.error('fetchPendingOrderSummary error:', e)
    dispatch(setCheckoutSummaryError(errorMessage))
    Sentry.captureException(e)
    return false
  }
}

// Update order quantity (for checkout - persists to backend)
export const updateOrderQuantity =
  (orderId, venueId, quantity) => async dispatch => {
    try {
      const url = `order/${orderId}`
      await API.patch('api', url, {
        body: { quantity, venueId },
      })

      // Refetch summary to get latest state from backend
      await dispatch(fetchPendingOrderSummary(venueId))
      return true
    } catch (e) {
      console.error('updateOrderQuantity error:', e)
      Sentry.captureException(e)
      throw e // Re-throw so component can handle error
    }
  }

// Delete order (for checkout - persists to backend)
export const deleteOrder = (orderId, venueId) => async dispatch => {
  try {
    const url = `order/${orderId}?venueId=${venueId}`
    await API.delete('api', url)

    // Refetch summary to get latest state from backend
    await dispatch(fetchPendingOrderSummary(venueId))
    return true
  } catch (e) {
    console.error('deleteOrder error:', e)
    Sentry.captureException(e)
    throw e // Re-throw so component can handle error
  }
}

const normalizeConfirmOrderResponse = response => {
  if (!response) return response

  let normalized = response

  if (typeof normalized.body === 'string') {
    try {
      normalized = JSON.parse(normalized.body)
    } catch (e) {
      // Keep original response shape if body is not JSON
    }
  } else if (normalized.body && typeof normalized.body === 'object') {
    normalized = normalized.body
  }

  if (normalized?.data && typeof normalized.data === 'object') {
    normalized = normalized.data
  }

  if (normalized?.result && typeof normalized.result === 'object') {
    normalized = normalized.result
  }

  return normalized
}

// Confirm order (Phase 3 - atomic send)
export const confirmOrder = (venueId, payload) => async dispatch => {
  try {
    const url = `venue/${venueId}/confirm-order`
    const response = await API.post('api', url, {
      body: payload,
    })

    const result = normalizeConfirmOrderResponse(response)
    if (result?.code === 'SUCCESS') {
      dispatch(setCheckoutSummary(null))
      dispatch(fetchOrders(venueId))
    }
    return result
  } catch (e) {
    console.error('confirmOrder error:', e)
    Sentry.captureException(e)
    throw e // Re-throw so component can handle error
  }
}

export default ordersSlice
